import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  ArrowUpRight,
  BarChart3,
  BellRing,
  Brain,
  Building2,
  Coins,
  CalendarDays,
ClipboardCheck,
  FileCheck2,
  FileText,
  Landmark,
  Layers,
  ListTodo,
  MessageSquare,
  Radio,
  Scale,
  Wallet,
  ShieldCheck,
  Bot,
} from "lucide-react";

import { usePermissions } from "@/contexts/PermissionsContext";
import { api } from "@/lib/api";
import SimplerCommandCenter from "@/components/dashboard/SimplerCommandCenter";

/**
 * Hub principal do Simpler.
 *
 * Organização visual inspirada na distribuição da Veri,
 * mantendo identidade, cores, módulos, rotas e permissões do Simpler.
 */
const HUB = [
  // Meu dia
  {
    key: "painel-360",
    label: "Painel 360°",
    icon: Building2,
    path: "/app/painel-360",
    perm: "diagnostico.view",
    tone: "text-primary",
    featured: true,
  },
  {
    key: "agenda",
    label: "Agenda Fiscal",
    icon: CalendarDays,
    path: "/app/agenda",
    perm: null,
    tone: "text-primary",
  },
  {
    key: "prioridades",
    label: "Central de Prioridades",
    icon: ListTodo,
    path: "/app/prioridades",
    perm: null,
    tone: "text-primary",
  },
  {
    key: "alertas",
    label: "Central de Alertas",
    icon: BellRing,
    path: "/app/alertas",
    perm: "alertas.view",
    tone: "text-primary",
  },

  // Monitoramento
  {
    key: "mensagens",
    label: "Mensagens",
    icon: MessageSquare,
    path: "/app/mensagens",
    perm: "mensagens.view",
    tone: "text-primary",
  },
  {
    key: "dashboard-federal",
    label: "Dashboard Federal",
    icon: Radio,
    path: "/app/dashboard-federal",
    perm: null,
    tone: "text-primary",
  },
  {
    key: "simples-nacional-apuracao",
    label: "Simples Nacional",
    icon: Coins,
    path: "/app/simples-nacional/apuracao",
    perm: "gestao_das.view",
    tone: "text-primary",
  },
  {
    key: "situacao-fiscal",
    label: "Situação Fiscal",
    icon: ShieldCheck,
    path: "/app/serpro/sitfis",
    perm: "situacao_fiscal.view",
    tone: "text-primary",
  },
  {
    key: "parcelamentos",
    label: "Parcelamentos",
    icon: Wallet,
    path: "/app/parcelamentos",
    perm: "parcelamentos.view",
    tone: "text-primary",
  },
  {
    key: "certidoes",
    label: "Certidões",
    icon: FileCheck2,
    path: "/app/certidoes",
    perm: "certidoes.view",
    tone: "text-primary",
  },

  // Raio-X Fiscal
  {
    key: "diagnostico",
    label: "Raio X Simpler",
    icon: ClipboardCheck,
    path: "/app/diagnostico",
    perm: "diagnostico.view",
    tone: "text-primary",
  },

  {
    key: "health-score",
    label: "Saúde Fiscal",
    desc: "Índice de Saúde Fiscal",
    icon: Activity,
    path: "/app/health-score",
    perm: "diagnostico.view",
    tone: "text-primary",
    bg: "bg-warning/10",
    border: "border-warning/30",
  },
  {
    key: "bi",
    label: "Simpler Bi",
    icon: BarChart3,
    path: "/app/bi",
    perm: "bi.view",
    tone: "text-primary",
  },
{
    key: "ia",
    label: "Sissi Assistente Fiscal",
    icon: Bot,
    path: "/app/ia",
    perm: "ia.view",
    tone: "text-primary",
  },
  {
    key: "relatorios",
    label: "Relatórios",
    icon: FileText,
    path: "/app/relatorios",
    perm: "relatorios.view",
    tone: "text-primary",
  },
  {
    key: "reforma",
    label: "Reforma Tributária",
    icon: Layers,
    path: "/app/health-score?tab=simulator",
    perm: null,
    tone: "text-primary",
  },
];

const GROUPS = [
  {
    key: "meu-dia",
    title: "Meu dia",
    subtitle:
      "",
    icon: ListTodo,
    iconClass:
      "border-primary/20 bg-primary/10 text-primary",
    hubPath: "/app/prioridades",
    slugs: [
      "painel-360",
      "agenda",
      "prioridades",
      "alertas",
    ],
  },
  {
    key: "monitoramento",
    title: "Monitoramento",
    subtitle:
      "",
    icon: Radio,
    iconClass:
      "border-primary/20 bg-primary/10 text-primary",
    hubPath: "/app/dashboard-federal",
    slugs: [
      "mensagens",
      "dashboard-federal",
    "simples-nacional-apuracao",
        "situacao-fiscal",
      "parcelamentos",
      "certidoes",
    ],
  },
  {
    key: "raio-x-fiscal",
    title: "Inteligência Fiscal",
    subtitle:
      "",
    icon: Activity,
    iconClass:
      "border-primary/20 bg-primary/10 text-primary",
    hubPath: "/app/diagnostico",
    slugs: [
      "diagnostico",
        "health-score",

      "ia",
      "relatorios",
      "reforma",
    ],
  },
];

export default function Hub() {
  const nav = useNavigate();
  const { hasPermission } = usePermissions();

  const [pending, setPending] = useState({
    total: 0,
    items: [],
    cta: null,
  });

  // CENTRAL_SIMPLER_HUB_DATA_V1
  const [commandCenterData, setCommandCenterData] = useState({
    metrics: null,
    messages: null,
    monitoring: null,
    certificates: null,
    obligations: null,
    sitfis: null,
  });

  useEffect(() => {
    let alive = true;

    api
      .get("/companies/pending-configuration?limit=5")
      .then((response) => {
        if (alive) {
          setPending(
            response.data || {
              total: 0,
              items: [],
            }
          );
        }
      })
      .catch(() => {
        // A ausência do banner não bloqueia o Hub.
      });

    return () => {
      alive = false;
    };
  }, []);

  // CENTRAL_SIMPLER_HUB_FETCH_V1
  useEffect(() => {
    let alive = true;

    Promise.allSettled([
      api.get("/dashboard/metrics"),
      api.get("/caixa-postal/summary"),
      api.get("/dashboard/monitoring"),
      api.get("/fiscal/certidoes/summary"),
      api.get("/fiscal/obrigacoes/ausencias/overview"),
      api.get("/integrations/serpro/sitfis/portfolio"),
    ]).then(([
      metricsResult,
      messagesResult,
      monitoringResult,
      certificatesResult,
      obligationsResult,
      sitfisResult,
    ]) => {
      if (!alive) return;

      setCommandCenterData({
        metrics:
          metricsResult.status === "fulfilled"
            ? metricsResult.value?.data
            : null,

        messages:
          messagesResult.status === "fulfilled"
            ? messagesResult.value?.data
            : null,

        monitoring:
          monitoringResult.status === "fulfilled"
            ? monitoringResult.value?.data
            : null,

        certificates:
          certificatesResult.status === "fulfilled"
            ? certificatesResult.value?.data
            : null,

        obligations:
          obligationsResult.status === "fulfilled"
            ? obligationsResult.value?.data
            : null,

        sitfis:
          sitfisResult.status === "fulfilled"
            ? sitfisResult.value?.data
            : null,
      });
    });

    return () => {
      alive = false;
    };
  }, []);

  const canSee = (module) =>
    !module.perm || hasPermission(module.perm);

  const visibleBySlug = new Map(
    HUB.filter(canSee).map((module) => [
      module.key,
      module,
    ])
  );

  // CENTRAL_SIMPLER_HUB_DERIVED_V1
  const commandCards =
    commandCenterData.metrics?.cards || {};

  const commandMessages =
    commandCenterData.messages || {
      unread: 0,
      critical: 0,
      high: 0,
    };

  const commandMonitoring =
    commandCenterData.monitoring || null;

  const commandCertificates =
    commandCenterData.certificates || {
      total: 0,
      regulares: 0,
      vencendo: 0,
      vencidas: 0,
    };

  // CENTRAL_CERTIDOES_IRREGULARES_V1
  const commandIrregularCertificates = Math.max(
    0,
    Number(commandCertificates.total || 0) -
      Number(commandCertificates.regulares || 0) -
      Number(commandCertificates.vencendo || 0)
  );


  // CENTRAL_OBRIGACOES_REAIS_V1
  const commandObligations =
    commandCenterData.obligations || {
      counts: {},
    };

  const commandObligationCounts =
    commandObligations?.counts || {};

  const commandObligationItems = [
    {
      key: "DCTF_WEB",
      title: "DCTFWeb",
    },
    {
      key: "DCTF",
      title: "DCTF",
    },
    {
      key: "PGDAS",
      title: "PGDAS",
    },
    {
      key: "DASN",
      title: "DASN",
    },
    {
      key: "DEFIS",
      title: "DEFIS",
    },
    {
      key: "EFD",
      title: "EFD",
    },
    {
      key: "ECF",
      title: "ECF",
    },
    {
      key: "DIRF",
      title: "DIRF",
    },
  ]
    .map((item) => {
      const count = Number(
        commandObligationCounts?.[item.key] || 0
      );

      return {
        id: `obrigacao-${item.key.toLowerCase()}`,
        title: item.title,
        description:
          count > 0
            ? "Ausência de declaração identificada"
            : "Nenhuma ausência identificada",
        count,
        severity:
          count > 0
            ? "warning"
            : "success",
        route: "/app/obrigacoes",
      };
    })
    .filter((item) => item.count > 0);

  const commandObligationTotal =
    commandObligationItems.reduce(
      (total, item) =>
        total + Number(item.count || 0),
      0
    );


  // CENTRAL_SITFIS_REAL_V1
  const commandSitfisItems =
    Array.isArray(commandCenterData.sitfis?.items)
      ? commandCenterData.sitfis.items
      : [];

  // SITFIS_OFFICIAL_STATUS_SOURCE_V3
  //
  // A Central usa exclusivamente fiscal_status para os
  // números principais. Ocorrências auxiliares permanecem
  // disponíveis no diagnóstico, sem mudar o selo oficial.
  const commandSitfisPending =
    commandSitfisItems.filter((item) => {
      const fiscalStatus = String(
        item?.fiscal_status || ""
      ).toLowerCase();

      return fiscalStatus === "irregular";
    }).length;

  const commandSitfisRegular =
    commandSitfisItems.filter((item) => {
      const fiscalStatus = String(
        item?.fiscal_status || ""
      ).toLowerCase();

      return fiscalStatus === "regular";
    }).length;

  const commandSitfisItemsForDrawer = [
    {
      id: "situacao-fiscal-regular",
      title: "Situação fiscal regular",
      description: "Empresas sem pendências fiscais identificadas",
      count: commandSitfisRegular,
      severity: "success",
      route: "/app/serpro/sitfis?status=regular",
    },
    {
      id: "situacao-fiscal-pendente",
      title: "Situação fiscal pendente",
      description: "Empresas com pendências RFB ou PGFN",
      count: commandSitfisPending,
      severity:
        commandSitfisPending > 0
          ? "critical"
          : "success",
      route: "/app/serpro/sitfis?status=irregular",
    },
  ];

  return (
    <div
      className="animate-fade-up space-y-7 pb-8"
      data-testid="hub-page"
    >
      {pending.total > 0 && (
        <PendingConfigBanner
          data={pending}
          onCta={() =>
            nav("/app/configuracoes?tab=procurador")
          }
        />
      )}

      {/* CENTRAL_SIMPLER_HOME_V1 */}
      <SimplerCommandCenter
        sisiImage="/sisi/sisi.png"
        data={{
          companies:
            commandCards.total_empresas || 0,

          monitored:
            commandMonitoring?.cards?.monitoradas ??
            commandCards.total_empresas ??
            0,

          critical:
            commandCards.total_pendencias || 0,

          fiscalRegular:
            commandSitfisRegular,

          fiscalPending:
            commandSitfisPending,

          obligations: commandObligationTotal,

          certificates:
            commandIrregularCertificates,

          certificatesRegular:
            Number(commandCertificates.regulares || 0),

          messages:
            commandMessages.unread || 0,

          financial: 0,

          lastSync: "automática",

          fiscalItems: commandSitfisItemsForDrawer,

          certificateItems: [
            {
              id: "home-certidoes-regulares",
              title: "Certidões regulares",
              description:
                Number(commandCertificates.regulares || 0) > 0
                  ? "Documentos sem pendências"
                  : "Nenhuma certidão regular",
              count:
                Number(commandCertificates.regulares || 0),
              severity:
                Number(commandCertificates.regulares || 0) > 0
                  ? "success"
                  : "info",
            },
            {
              id: "home-certidoes-irregulares",
              title: "Certidões irregulares",
              description:
                commandIrregularCertificates > 0
                  ? "Certidões com situação irregular"
                  : "Nenhuma certidão irregular",
              count:
                commandIrregularCertificates,
              severity:
                commandIrregularCertificates > 0
                  ? "critical"
                  : "success",
            },
          ],

          messageItems: [
            {
              id: "mensagens-ecac",
              title: "e-CAC",
              description:
                commandMessages.unread > 0
                  ? "Mensagens da Caixa Postal Fiscal"
                  : "Caixa Postal Fiscal",
              count:
                commandMessages.unread || 0,
              severity:
                commandMessages.critical > 0
                  ? "critical"
                  : commandMessages.unread > 0
                  ? "info"
                  : "success",
            },
            {
              id: "mensagens-malha-fiscal",
              title: "Malha Fiscal",
              description:
                "Comunicações e ocorrências da Malha Fiscal",
              severity: "info",
            },
            {
              id: "mensagens-termo-receita",
              title: "Termo Receita Federal",
              description:
                "Termos e comunicações da Receita Federal",
              severity: "info",
            },
          ],

          obligationItems: commandObligationItems,

          financialItems: [],
        }}
        onNavigate={(route) => nav(route)}
      />

      {GROUPS.map((group) => {
        const items = group.slugs
          .map((slug) => visibleBySlug.get(slug))
          .filter(Boolean);

        if (items.length === 0) {
          return null;
        }

        const GroupIcon = group.icon;

        return (
          <section
            key={group.key}
            data-testid={`hub-group-${group.key}`}
          >
            <div className="mb-3 flex items-center justify-between gap-4 border-b border-border/70 pb-3">
              <div className="flex min-w-0 items-start gap-3">
                <div
                  className={`
                    flex
                    h-10
                    w-10
                    shrink-0
                    items-center
                    justify-center
                    rounded-xl
                    border
                    ${group.iconClass}
                  `}
                >
                  <GroupIcon
                    className="h-5 w-5"
                    strokeWidth={1.8}
                  />
                </div>

                <div className="flex h-10 min-w-0 items-center">
                  <h2 className="text-[17px] font-semibold leading-6 text-foreground">
                    {group.title}
                  </h2>
                </div>
              </div>

              <button
                type="button"
                onClick={() => nav(group.hubPath)}
                className="
                  hidden
                  shrink-0
                  items-center
                  gap-1
                  rounded-md
                  px-2
                  py-1
                  text-xs
                  font-medium
                  text-primary
                  transition-colors
                  hover:bg-primary/5
                  sm:inline-flex
                "
              >
                Ver hub

                <ArrowRight
                  className="h-3.5 w-3.5"
                  strokeWidth={1.8}
                />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {items.map((module) => (
                  <HubCard
                    key={module.key}
                    module={module}
                    onOpen={() => nav(module.path)}
                  />
                ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function HubCard({ module, onOpen }) {
  const Icon = module.icon;

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`hub-card-${module.key}`}
      className={`
        group
        relative
        flex
        min-h-[84px]
        w-full
        items-center
        gap-3
        overflow-hidden
        rounded-xl
        border
        px-4
        py-3
        text-left
        shadow-sm
        transition-all
        duration-150
        ${
          module.featured
            ? `
              border-primary
              bg-primary
              text-primary-foreground
              hover:-translate-y-0.5
              hover:shadow-md
            `
            : `
              border-border
              bg-card
              text-foreground
              hover:-translate-y-0.5
              hover:border-primary/35
              hover:shadow-md
            `
        }
      `}
    >
      <Icon
        className={`
          h-5
          w-5
          shrink-0
          ${
            module.featured
              ? "text-primary-foreground"
              : module.tone
          }
        `}
        strokeWidth={1.9}
      />

      <div
        className={`
          min-w-0
          flex-1
          text-[14px]
          font-semibold
          leading-5
          ${
            module.featured
              ? "text-primary-foreground"
              : "text-foreground"
          }
        `}
      >
        {module.label}
      </div>

      <ArrowUpRight
        className={`
          h-4
          w-4
          shrink-0
          opacity-0
          transition-all
          duration-150
          group-hover:-translate-y-0.5
          group-hover:translate-x-0.5
          group-hover:opacity-100
          ${
            module.featured
              ? "text-primary-foreground/80"
              : "text-muted-foreground"
          }
        `}
        strokeWidth={1.8}
      />
    </button>
  );
}

function PendingConfigBanner({ data, onCta }) {
  const {
    total,
    items = [],
    cta,
  } = data;

  return null; // banner procurador desativado
  // eslint-disable-next-line no-unreachable
  return (
    <div
      data-testid="hub-pending-config-banner"
      className="rounded-xl border border-warning/40 bg-warning/5 p-5"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-primary/10 text-primary">
            <AlertTriangle
              className="h-5 w-5"
              strokeWidth={1.8}
            />
          </div>

          <div className="min-w-0">
            <div className="text-sm font-semibold text-foreground">
              {total} empresa
              {total === 1 ? "" : "s"} pendente
              {total === 1 ? "" : "s"} de configuração
            </div>

            <div className="mt-1 max-w-2xl text-xs text-muted-foreground">
              Integrações SERPRO bloqueadas por falta de
              Procurador vinculado ao escritório. Nenhum
              dado é sincronizado até que a configuração
              seja concluída.
            </div>

            {items.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {items.slice(0, 5).map((company) => (
                  <span
                    key={company.id}
                    className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 text-[11px] text-muted-foreground"
                    data-testid={`pending-company-${company.id}`}
                  >
                    {company.razao_social || company.cnpj}
                  </span>
                ))}

                {total > items.length && (
                  <span className="self-center text-[11px] text-muted-foreground">
                    +{total - items.length} outra
                    {total - items.length === 1
                      ? ""
                      : "s"}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={onCta}
          data-testid="hub-cta-link-procurador"
          className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
        >
          {cta?.label || "Vincular Procurador"}

          <ArrowUpRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
